import React from 'react';
import { ReviewCycle, ReviewCycleStatus, AuditLogEntry } from '../../types/pms';
import './ObjectiveLockManager.css';

interface ObjectiveLockManagerProps {
  /** All review cycles */
  reviewCycles: ReviewCycle[];
  /** Audit log entries for lock/unlock actions */
  auditLog: AuditLogEntry[];
  /** Lock objectives for a cycle */
  onLock: (cycleId: string) => void;
  /** Unlock objectives for a cycle */
  onUnlock: (cycleId: string) => void;
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * ObjectiveLockManager Component
 * Lock/unlock objectives for a review cycle with audit log display.
 * Requirements: 15.1, 15.2, 15.3, 15.4
 */
const ObjectiveLockManager: React.FC<ObjectiveLockManagerProps> = ({
  reviewCycles,
  auditLog,
  onLock,
  onUnlock,
}) => {
  // Only show active or recently completed cycles
  const manageable = reviewCycles.filter(
    (rc) => rc.status === ReviewCycleStatus.ACTIVE || rc.status === ReviewCycleStatus.LOCKED
  );

  const lockEntries = auditLog.filter(
    (e) => e.action === 'OBJECTIVES_LOCKED' || e.action === 'OBJECTIVES_UNLOCKED'
  );

  return (
    <div className="pms-lock-manager">
      <h1 className="pms-lock-manager__title">Objective Lock Management</h1>

      {/* Cycles */}
      <section className="pms-lock-manager__section" aria-label="Review Cycles">
        <h2 className="pms-lock-manager__section-title">Review Cycles</h2>
        {manageable.length === 0 ? (
          <p className="pms-lock-manager__empty">No active review cycles to manage.</p>
        ) : (
          <div className="pms-lock-manager__cycles">
            {manageable.map((cycle) => (
              <div key={cycle.id} className="pms-lock-cycle-card">
                <div className="pms-lock-cycle-card__info">
                  <h3 className="pms-lock-cycle-card__name">{cycle.name}</h3>
                  <div className="pms-lock-cycle-card__status-row">
                    {cycle.isLocked ? (
                      <span className="pms-lock-indicator pms-lock-indicator--locked">🔒 Locked</span>
                    ) : (
                      <span className="pms-lock-indicator pms-lock-indicator--unlocked">🔓 Unlocked</span>
                    )}
                  </div>
                </div>
                <div className="pms-lock-cycle-card__actions">
                  {cycle.isLocked ? (
                    <button
                      className="pms-lock-btn pms-lock-btn--unlock"
                      onClick={() => onUnlock(cycle.id)}
                    >
                      Unlock Objectives
                    </button>
                  ) : (
                    <button
                      className="pms-lock-btn pms-lock-btn--lock"
                      onClick={() => onLock(cycle.id)}
                    >
                      Lock Objectives
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Audit Log (Req 15.4) */}
      <section className="pms-lock-manager__section" aria-label="Audit Log">
        <h2 className="pms-lock-manager__section-title">Audit Log</h2>
        {lockEntries.length === 0 ? (
          <p className="pms-lock-manager__empty">No lock/unlock actions recorded.</p>
        ) : (
          <div className="pms-lock-audit-list">
            {lockEntries
              .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
              .map((entry) => (
                <div key={entry.id} className="pms-lock-audit-item">
                  <span className={`pms-lock-audit-item__action ${entry.action === 'OBJECTIVES_LOCKED' ? 'pms-lock-audit-item__action--lock' : 'pms-lock-audit-item__action--unlock'}`}>
                    {entry.action === 'OBJECTIVES_LOCKED' ? '🔒 Locked' : '🔓 Unlocked'}
                  </span>
                  <span className="pms-lock-audit-item__details">{entry.details}</span>
                  <span className="pms-lock-audit-item__meta">
                    {entry.performedByName} · {formatDateTime(entry.timestamp)}
                  </span>
                </div>
              ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ObjectiveLockManager;
